import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjayNarrator } from '../../mascot';
import { dataLayer } from '../../data/dataLayer';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { useTourAnchor } from './onboardingTour/useTourAnchor';
interface TourStep {
  id: string;
  view: string;
  title: string;
  message: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}
const TOUR_STEPS: TourStep[] = [{
  id: 'tour-home',
  view: 'home',
  title: 'Jouw Dashboard',
  message: 'Hier begin je elke dag! Je ziet hier je dagelijkse doel, je streak en een slimme tip om te oefenen.',
  position: 'right'
}, {
  id: 'tour-modules',
  view: 'modules',
  title: 'Mijn Werelden',
  message: 'Hier vind je alle vakken zoals Rekenen en Taal. Kies een wereld en ontdek nieuwe onderwerpen!',
  position: 'right'
}, {
  id: 'tour-learningLab',
  view: 'learningLab',
  title: 'Het Oefenlab',
  message: 'Wil je gericht trainen voor een toets of extra uitdaging? In het Oefenlab vind je speciale missies.',
  position: 'right'
}, {
  id: 'tour-progress',
  view: 'progress',
  title: 'Jouw Voortgang',
  message: 'Bekijk hier hoe goed je gaat! Je ziet je XP, je groei en de prestaties die je hebt behaald.',
  position: 'right'
}, {
  id: 'tour-collection',
  view: 'collection',
  title: 'Mijn Collectie',
  message: 'Verdien XP om nieuwe looks voor mij te ontgrendelen! Hier kun je me aankleden zoals jij wilt.',
  position: 'right'
}, {
  id: 'tour-settings',
  view: 'settings',
  title: 'Instellingen',
  message: 'Hier kun je geluiden aanpassen, je dagdoel veranderen of deze rondleiding opnieuw bekijken.',
  position: 'right'
}];

/** Breedte van het uitlegblok. Staat hier omdat de opmaak eronder hem ook
 *  gebruikt (`max-w`), en de plaatsingslogica moet weten of het blok ergens
 *  past. Twee losse waarden die uiteenlopen is precies hoe een blok half
 *  buiten beeld belandt. */
const POPOVER_WIDTH = 340;

/** Geschatte hoogte, alleen om te bepalen of het blok ergens past. Exact hoeft
 *  niet: de plaatsing wordt daarna binnen het venster geklemd. */
const POPOVER_HEIGHT = 260;
interface OnboardingTourProps {
  currentView: string;
  onNavigate: (view: string) => void;
}
export function OnboardingTour({
  currentView,
  onNavigate
}: OnboardingTourProps) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const {
    shouldAnimate
  } = useMotionBudget();
  const activeStep = TOUR_STEPS[currentStepIndex];
  // De positie komt volledig uit de hook. `anchor === null` betekent "nog aan
  // het meten" — en dan tekent deze component niets dat een plek suggereert.
  const anchor = useTourAnchor({
    targetId: activeStep.id,
    preferred: activeStep.position,
    enabled: isActive && !isFinished && !isNavigating && currentView === activeStep.view
  }, POPOVER_WIDTH, POPOVER_HEIGHT);
  // Use a ref for onNavigate so the mount effect doesn't re-run when it changes
  const onNavigateRef = useRef(onNavigate);
  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);
  // 1. Start the tour ONCE on mount if the preference is set.
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;
    const checkTour = async () => {
      try {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs.pendingAppTour) {
          setIsActive(true);
          setCurrentStepIndex(0);
          setIsNavigating(true);
          onNavigateRef.current(TOUR_STEPS[0].view);
        }
      } catch {

        /* non-fatal */}
    };
    checkTour();
  }, []);
  // 2. Choreograph navigation and spotlight targeting.
  useEffect(() => {
    if (!isActive || isFinished) return;
    const step = TOUR_STEPS[currentStepIndex];
    // If we haven't reached the target view yet, wait.
    if (currentView !== step.view) {
      // Safety: if we aren't navigating but the view is wrong, force it.
      if (!isNavigating) {
        setIsNavigating(true);
        onNavigateRef.current(step.view);
      }
      return;
    }
    // We arrived at the correct view. Het opmeten en plaatsen doet
    // `useTourAnchor`; deze effect gaat alleen nog over navigatie.
    setIsNavigating(false);
  }, [isActive, isFinished, currentStepIndex, currentView, isNavigating]);
  // Escape sluit de rondleiding. De overlay dekt het hele scherm en ving geen
  // enkele toets: wie hem niet wilde, kon er met het toetsenbord niet uit —
  // "Sla over" was alleen met de muis te bereiken.
  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        void completeTourRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive]);
  const completeTour = async () => {
    try {
      const prefs = await dataLayer.getLearnerPreferences();
      await dataLayer.saveLearnerPreferences({
        ...prefs,
        pendingAppTour: false,
        tourCompleted: true
      });
    } catch {

      /* non-fatal */}
    setIsActive(false);
    setIsFinished(false);
    onNavigateRef.current('home');
  };
  // Via een ref, zodat de Escape-listener niet bij elke render opnieuw wordt
  // aangehangen.
  const completeTourRef = useRef(completeTour);
  useEffect(() => {
    completeTourRef.current = completeTour;
  });
  const handleNext = () => {
    if (isNavigating) return;
    soundManager.playCorrect();
    hapticManager.hapticMedium();
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setIsNavigating(true);
      onNavigateRef.current(TOUR_STEPS[nextIndex].view);
    } else {
      soundManager.playLevelUp();
      hapticManager.hapticSuccess();
      setIsFinished(true);
    }
  };
  const handlePrev = () => {
    if (isNavigating) return;
    soundManager.playNavigate();
    hapticManager.hapticLight();
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      setIsNavigating(true);
      onNavigateRef.current(TOUR_STEPS[prevIndex].view);
    }
  };
  const handleSkip = async () => {
    soundManager.playNavigate();
    await completeTour();
  };
  if (!isActive) return null;
  const step = TOUR_STEPS[currentStepIndex];
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const targetRect = anchor?.rect ?? null;

  // Zolang de positie niet vaststaat, tekenen we niets dat een plek suggereert.
  // Dat is de kern van de reparatie: eerder werd het blok meteen gerenderd,
  // viel het zonder rect terug op "midden op het scherm", en sprong het naar
  // zijn echte plek zodra de meting binnenkwam — elke stap opnieuw.
  const isMeasuring = anchor === null && !isFinished;
  const spotlightStyle = targetRect ? {
    left: targetRect.left - 8,
    top: targetRect.top - 8,
    width: targetRect.width + 16,
    height: targetRect.height + 16
  } : {
    left: 0,
    top: 0,
    width: 0,
    height: 0
  };

  // Op mobiel een vaste balk onderaan; op desktop de plaatsing die de hook
  // heeft uitgerekend. Geen `transform` meer bij de gecentreerde variant: het
  // wisselen tussen wél en géén transform was zelf een sprong.
  let popoverStyle: React.CSSProperties;
  let popoverClass: string;
  if (isMobile) {
    popoverStyle = {
      bottom: 24,
      left: 16,
      right: 16,
      width: 'auto',
      maxWidth: 'none'
    };
    popoverClass = 'items-center text-center';
  } else if (!anchor || anchor.placement === 'center') {
    popoverStyle = {
      left: '50%',
      top: '50%',
      x: '-50%',
      y: '-50%'
    };
    popoverClass = 'items-center text-center';
  } else {
    popoverStyle = {
      left: anchor.left,
      top: anchor.top
    };
    popoverClass = anchor.placement === 'anchored-right' ? 'items-start' : 'items-center';
  }
  return <AnimatePresence data-id="element-2592" data-mp="13yx79pzapg8a">
      <div className="fixed inset-0 z-[100] pointer-events-auto" data-id="element-2593" data-mp="13ydl1x2m45og">
        {/* SVG overlay with spotlight cutout (Desktop only) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-2594" data-mp="yzatnffk1xyy">
          <defs data-id="element-2595" data-mp="1wfhqno3s1dot">
            <mask id="spotlight-mask" data-id="element-2596" data-mp="1wfzp8rdww3zw">
              <rect width="100%" height="100%" fill="white" data-id="element-2597" data-mp="1wh11gz6nywj7" />
              {targetRect && !isMobile && !isFinished && !isNavigating && <motion.rect initial={false} animate={{
              x: spotlightStyle.left,
              y: spotlightStyle.top,
              width: spotlightStyle.width,
              height: spotlightStyle.height
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} rx="12" fill="black" data-id="element-2598" data-mp="1z05ur3i6c2ik" />}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15, 23, 42, 0.75)" mask={!isMobile ? 'url(#spotlight-mask)' : undefined} className="backdrop-blur-sm" data-id="element-2599" data-mp="2hz1q4i5rqmxz" />
          
        </svg>

        {isFinished ? <div className="absolute inset-0 flex items-center justify-center p-4" data-id="element-2600" data-mp="2rnrzwg71vf7j">
            <ConfettiCelebration data-id="element-2601" data-mp="2wnek59lapvpu" />
            <motion.div initial={shouldAnimate ? {
          opacity: 0,
          scale: 0.9,
          y: 20
        } : false} animate={shouldAnimate ? {
          opacity: 1,
          scale: 1,
          y: 0
        } : false} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative z-10 flex flex-col items-center text-center" data-id="element-2602" data-mp="2wmd0igq7s0wi">
            
              <div className="w-24 h-24 mb-6" data-id="element-2603" data-mp="oakzge50ipf1">
                <DjayNarrator emotion="celebrating" size="xl" data-id="element-2604" data-mp="obdx8plzu4l1" />
              </div>
              <h2 className="text-3xl font-black text-slate-900 mb-3" data-id="element-2605" data-mp="t8w7cjbqypum">
                Je bent er klaar voor!
              </h2>
              <p className="text-slate-600 mb-8 text-lg" data-id="element-2606" data-mp="y1yua8m0r11u">
                Heel veel succes en plezier met oefenen. Je kunt deze
                rondleiding altijd nog eens bekijken via Instellingen.
              </p>
              <Button onClick={completeTour} size="lg" className="w-full py-6 text-lg font-bold" data-id="element-2607" data-mp="1736puix5hns8">
              
                Start met oefenen!
                <Check className="w-6 h-6 ml-2" data-id="element-2608" data-mp="1gygs97rgnx6j" />
              </Button>
            </motion.div>
          </div> : isMeasuring ? null : <motion.div
      // `key` per stap: zonder key hergebruikt React hetzelfde element en
      // verspringt de nieuwe positie in één frame. Met key verdwijnt het
      // oude blok en komt het nieuwe op zijn plek in beeld.
      key={step.id} role="dialog" aria-modal="true" aria-label={`Rondleiding, stap ${currentStepIndex + 1} van ${TOUR_STEPS.length}: ${step.title}`} className={`absolute flex flex-col w-full p-4 ${popoverClass}`} style={{
        maxWidth: POPOVER_WIDTH,
        ...popoverStyle
      }} initial={shouldAnimate ? {
        opacity: 0,
        scale: 0.96
      } : false} animate={shouldAnimate ? {
        opacity: 1,
        scale: 1
      } : false}
      // Geen `exit`: de directe ouder is geen AnimatePresence, dus die
      // zou nooit draaien. Bij een stapwissel verdwijnt het oude blok
      // meteen en komt het nieuwe op zijn plek in beeld.
      // 200ms met een uitlopende curve: de uitleg hoort te verschijnen,
      // niet te stuiteren. De veer die hier stond gaf een naveering die
      // als "flitsen" leest wanneer hij per stap opnieuw gebeurt.
      transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-2609" data-mp="3dtcxq6gs44a7">
          
            <div className="flex flex-col items-center mb-4" data-id="element-2610" data-mp="vuuwxut4sdir">
              <motion.div animate={shouldAnimate ? {
            y: [0, -5, 0]
          } : false} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2611" data-mp="zwgf9vofpk0c">
              
                <DjayNarrator emotion={isNavigating ? 'thinking' : 'excited'} size="lg" data-id="element-2612" data-mp="qtyyvxvgnll9" />
              
              </motion.div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border-2 border-orange-100 overflow-hidden w-full" data-id="element-2613" data-mp="gx2mvi6cshby">
              <div className="p-5" data-id="element-2614" data-mp="367j2kapoqf8">
                <div className="flex items-center justify-between mb-2" data-id="element-2615" data-mp="378vot7dlkz2">
                  <h3 className="font-black text-lg text-slate-900" data-id="element-2616" data-mp="83zcjir08klj">
                    {step.title}
                  </h3>
                  <span className="text-xs font-bold text-orange-500 bg-orange-100 px-2 py-1 rounded-full" data-id="element-2617" data-mp="3pdfkjbiigd5z">
                    {currentStepIndex + 1}/{TOUR_STEPS.length}
                  </span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed min-h-[40px]" data-id="element-2618" data-mp="3aqgsbebtxeq7">
                  {step.message}
                </p>
              </div>
              <div className="bg-slate-50 p-3 flex items-center justify-between border-t border-slate-100" data-id="element-2619" data-mp="3komtftptagu0">
                <button onClick={handleSkip} disabled={isNavigating} className="text-xs font-bold text-slate-400 hover:text-slate-600 px-2 py-2 disabled:opacity-50" data-id="element-2620" data-mp="3fo8jg64ngwtc">
                
                  Sla over
                </button>
                <div className="flex gap-2" data-id="element-2621" data-mp="3pldbgml6fmi2">
                  <Button variant="outline" size="sm" onClick={handlePrev} disabled={currentStepIndex === 0 || isNavigating} className="px-3" data-id="element-2622" data-mp="7a02jvbhpuh0">
                  
                    <ArrowLeft className="w-4 h-4" data-id="element-2623" data-mp="2cq27kmkbyee" />
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleNext} disabled={isNavigating} className="px-4 min-w-[100px]" data-id="element-2624" data-mp="h7wa6d57y7ad">
                  
                    {isNavigating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" data-id="element-2625" data-mp="25q8veg0d2srv" /> : currentStepIndex === TOUR_STEPS.length - 1 ? 'Klaar' : <>
                        Volgende
                        <ArrowRight className="w-4 h-4 ml-1" data-id="element-2626" data-mp="2fhd97ceaw7l4" />
                      </>}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>}
      </div>
    </AnimatePresence>;
}